import { useNavigate } from '@tanstack/react-router';
import { Button } from 'components/ui/Button';
import { SectionLabel } from 'components/ui/SectionLabel';
import { TypeWriter } from 'components/ui/TypeWriter';
import { RouterKey } from 'consts';
import { useLanguage } from 'context/language.context';
import { StringKey } from 'dictionaries/string-key.enum';
import { cn } from 'lib/utils';
import { motion } from 'motion/react';
import { useMemo } from 'react';

export const ProductSection = () => {
  const navigate = useNavigate();
  const { t, direction } = useLanguage();

  const isSafari = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('safari') && !userAgent.includes('chrome');
  }, []);

  return (
    <section
      id="product"
      className="relative w-full overflow-x-hidden bg-[radial-gradient(53.63%_53.63%_at_50%_100%,#1e182a_0%,#0A080F_100%)]"
    >
      <img className="absolute hidden md:block" src="/assets/product-bg.png" alt="product-bg" />
      <img
        className="absolute block md:hidden"
        src="/assets/product-bg-mobile.png"
        alt="product-bg-mobile"
      />
      <div className="relative lg:px-20 mx-4 sm:mx-28 flex flex-col items-center pt-16 md:pt-24">
        <SectionLabel>{t(StringKey.THE_PLACE_IS_YOURS)}</SectionLabel>
        <h1 className="relative bg-clip-text bg-label-gradient mt-4 text-transparent font-semibold text-4xl leading-[44px] md:text-[60px] md:leading-[72px] text-center">
          <TypeWriter
            text={t(StringKey.ALL_IN_ONE_HUB)}
            className={cn(
              'after:h-11 md:after:h-[72px]',
              direction === 'rtl' ? 'after:bg-[#B097E9]' : 'after:bg-[#6B9BC7]',
            )}
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, scale: 0.8, x: -16, y: 24, rotate: 8 }}
          animate={{
            opacity: 1,
            scale: 1,
            x: 0,
            y: 0,
            rotate: 0,
            transition: { ease: 'easeOut', duration: 0.6, delay: 1.2 },
          }}
          className="mt-4 md:mx-28 md:mt-6 text-m md:text-xl text-center"
        >
          {t(StringKey.MAAZEEB_PLATFORM_DESC)}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{
            opacity: 1,
            scale: 1,
            transition: {
              ease: 'easeOut',
              duration: 0.6,
              delay: 1.5,
            },
          }}
          className="flex flex-col-reverse xs:flex-row w-full z-1 justify-center gap-3 mt-8 md:mt-12"
        >
          <Button
            variant="tertiary"
            className="max-xs:grow px-5 py-3 md:px-[26px] md:py-4"
            onClick={() => navigate({ to: RouterKey.REQUEST_DEMO })}
          >
            {t(StringKey.LEARN_MORE)}
          </Button>
          <Button
            className="max-xs:grow px-5 py-3 md:px-[26px] md:py-4"
            onClick={() => navigate({ to: RouterKey.REGISTER_NOW })}
          >
            {t(StringKey.REGISTER_NOW)}
          </Button>
        </motion.div>
      </div>
      <div className="flex flex-col justify-end w-full h-fit max-h-fit relative mt-4 md:px-20">
        <img
          className="max-md:hidden absolute z-0 left-0 w-full object-cover"
          src="/assets/laptop-bg.png"
          alt="laptop-background"
        />
        {isSafari ? (
          <img
            className={cn(
              'relative z-1 object-cover max-md:min-h-[400px] md:w-[80%]',
              direction === 'rtl'
                ? 'max-md:left-12 md:right-0 object-left'
                : 'max-md:right-12 md:left-0 object-right',
            )}
            src={`/assets/laptop${direction === 'rtl' ? '-ar' : ''}.png`}
            alt="laptop"
          />
        ) : (
          <div className="relative z-0 flex justify-start items-center w-full aspect-[1/0.46] max-md:min-h-[450px] overflow-hidden">
            <video
              className={cn(
                'absolute w-full md:w-[90%] h-[105%] object-cover -bottom-[14%] md:-bottom-[12%]',
                direction === 'rtl'
                  ? 'max-md:object-[left_-48px_top_50%] object-center rotate-2'
                  : 'max-md:object-[right_-48px_top_50%] object-center -rotate-2',
              )}
              src={`/assets/laptop-video${direction === 'rtl' ? '-ar' : ''}.webm`}
              muted
              preload="auto"
              autoPlay
            />
          </div>
        )}
      </div>
    </section>
  );
};
